<template>
  <div
    ref="board"
    class="dashboard"
    :class="{ 'dashboard--tv': isFullscreen }"
  >
    <div class="dashboard__header">
      <div>
        <h3 class="dashboard__title mb-0">
          {{ $t('dashboard.title') }}
          <small
            v-if="nodeName"
            class="text-muted"
          >· {{ nodeName }}</small>
        </h3>
        <div
          class="dashboard__updated"
          :class="{ 'text-danger': stale }"
        >
          <feather-icon
            :icon="stale ? 'WifiOffIcon' : 'RefreshCwIcon'"
            size="12"
            class="mr-25"
          />
          <span v-if="stale">{{ $t('dashboard.stale') }} · </span>
          {{ $t('dashboard.updatedAt') }} {{ updatedAtText }}
        </div>
      </div>
      <button
        type="button"
        class="btn btn-outline-primary btn-sm"
        @click="toggleFullscreen"
      >
        <feather-icon
          :icon="isFullscreen ? 'Minimize2Icon' : 'Maximize2Icon'"
          size="14"
          class="mr-25"
        />
        {{ isFullscreen ? $t('dashboard.exitFullscreen') : $t('dashboard.fullscreen') }}
      </button>
    </div>

    <b-alert
      :show="!!loadError && !d.generated_at"
      variant="danger"
      class="mb-2"
    >
      <div class="alert-body">
        <feather-icon
          icon="AlertOctagonIcon"
          class="mr-50"
        />
        {{ $t('dashboard.loadError') }}: {{ loadError }}
      </div>
    </b-alert>

    <!-- Contadores -->
    <div class="row">
      <div class="col-12 col-sm-6 col-xl mb-2">
        <kpi-tile
          :label="$t('dashboard.kpi.active')"
          :value="d.active_total"
          icon="AlertCircleIcon"
          variant="primary"
          :loading="loading"
        >
          <div class="dashboard__steps">
            <span
              v-for="s in activeSteps"
              :key="s.key"
              class="dashboard__step"
            >
              <span
                class="dashboard__step-dot"
                :style="{ background: s.color }"
              />
              {{ s.label }} <strong>{{ s.total }}</strong>
            </span>
          </div>
        </kpi-tile>
      </div>
      <div class="col-12 col-sm-6 col-xl mb-2">
        <kpi-tile
          :label="$t('dashboard.kpi.month')"
          :value="d.month_total"
          icon="CalendarIcon"
          variant="info"
          :hint="$t('dashboard.kpi.monthHint', { today: fmt(d.today_total), finalized: fmt(d.finalized_today) })"
          :loading="loading"
        />
      </div>
      <div class="col-12 col-sm-6 col-xl mb-2">
        <kpi-tile
          :label="$t('dashboard.kpi.freeTeams')"
          :value="teams.available"
          icon="TruckIcon"
          :variant="teams.on_duty && !teams.available ? 'danger' : 'success'"
          :hint="$t('dashboard.kpi.teamsHint', { onDuty: fmt(teams.on_duty), busy: fmt(teams.busy), blocked: fmt(teams.blocked) })"
          :loading="loading"
        />
      </div>
      <div class="col-12 col-sm-6 col-xl mb-2">
        <kpi-tile
          :label="$t('dashboard.kpi.pendingRequests')"
          :value="d.pending_requests"
          icon="PhoneForwardedIcon"
          :variant="d.pending_requests ? 'warning' : 'secondary'"
          :hint="d.pending_requests ? $t('dashboard.kpi.oldestPending', { time: formatSeconds(d.oldest_pending_seconds) }) : null"
          :loading="loading"
        />
      </div>
      <div class="col-12 col-sm-6 col-xl mb-2">
        <kpi-tile
          :label="$t('dashboard.kpi.delayed')"
          :value="d.delayed_open"
          icon="AlertTriangleIcon"
          :variant="d.delayed_open ? 'danger' : 'success'"
          :pulse="!!d.delayed_open"
          :hint="$t('dashboard.kpi.delayedHint')"
          :loading="loading"
        />
      </div>
    </div>

    <!-- Tempos médios (hoje) -->
    <h6 class="dashboard__section">
      {{ $t('dashboard.avgTitle') }}
    </h6>
    <div class="row">
      <div
        v-for="t in avgTiles"
        :key="t.key"
        class="col-12 col-sm-6 col-xl-3 mb-2"
      >
        <kpi-tile
          :label="t.label"
          :value="formatSeconds(t.value)"
          :icon="t.icon"
          :variant="t.variant"
          :hint="t.hint"
          :loading="loading"
        />
      </div>
    </div>

    <!-- Distribuições -->
    <div class="row">
      <div class="col-12 col-lg-6 mb-2">
        <b-card
          class="h-100 mb-0"
          :title="$t('dashboard.byClassification')"
        >
          <distribution-bar-chart
            :items="d.by_classification || []"
            :series-name="$t('dashboard.occurrences')"
            :empty-text="$t('dashboard.emptyOpen')"
          />
        </b-card>
      </div>
      <div class="col-12 col-lg-6 mb-2">
        <b-card
          class="h-100 mb-0"
          :title="$t('dashboard.byInvolvedRisk')"
        >
          <distribution-bar-chart
            :items="d.by_involved_risk || []"
            :series-name="$t('dashboard.involveds')"
            :empty-text="$t('dashboard.emptyInvolved')"
          />
        </b-card>
      </div>
    </div>

    <div class="row">
      <div class="col-12 mb-2">
        <b-card
          class="mb-0"
          :title="$t('dashboard.hourly')"
        >
          <hourly-chart
            :today="hourly.today"
            :yesterday="hourly.yesterday"
            :today-label="$t('dashboard.today')"
            :yesterday-label="$t('dashboard.yesterday')"
          />
        </b-card>
      </div>
    </div>
  </div>
</template>

<script>
import { BAlert, BCard } from 'bootstrap-vue'
import KpiTile from '@/components/dashboard/KpiTile.vue'
import DistributionBarChart from '@/components/dashboard/DistributionBarChart.vue'
import HourlyChart from '@/components/dashboard/HourlyChart.vue'
import ApiErrorMixin from '@/mixins/ApiErrorMixin'
import RealtimeResyncMixin from '@/mixins/RealtimeResyncMixin'
import OccurrenceFeedMixin from '@/mixins/OccurrenceFeedMixin'
import DispatchEventsMixin from '@/mixins/DispatchEventsMixin'
import { resolveStepThresholds } from '@/mixins/ElapsedTimeMixin'
import { formatSeconds } from '@/utils/duration'
import { serverNow } from '@/libs/serverTime'

// Fallback para o que não gera evento (embarque/desembarque, virada do dia)
const FALLBACK_MS = 60000
// Agrupa rajadas de eventos numa única requisição
const EVENT_DEBOUNCE_MS = 3000
// TTL do cache do backend (DashboardService::TTL) + folga
const STALE_RETRY_MS = 5500

const STEP_COLORS = {
  solicita: '#6B7280',
  regulacao: '#2563EB',
  despacha: '#7C3AED',
  liberado: '#16A34A',
}

export default {
  name: 'Home',
  components: {
    BAlert, BCard, KpiTile, DistributionBarChart, HourlyChart,
  },
  mixins: [ApiErrorMixin, RealtimeResyncMixin, OccurrenceFeedMixin, DispatchEventsMixin],
  data() {
    return {
      d: {},
      loading: true,
      stale: false,
      loadError: null,
      requesting: false,
      reloadQueued: false,
      lastEventAt: 0,
      staleRetryTimer: null,
      fallbackTimer: null,
      isFullscreen: false,
      teamRequestedChannel: null,
      teamRequestedListener: null,
    }
  },
  computed: {
    nodeId() {
      const node = this.$store.state.selected_node
      return node ? node.id : null
    },
    nodeName() {
      const node = this.$store.state.selected_node
      return node ? node.name : null
    },
    teams() {
      return this.d.teams || {}
    },
    hourly() {
      return this.d.hourly || { today: [], yesterday: [] }
    },
    updatedAtText() {
      if (!this.d.generated_at) return '—'
      return new Date(this.d.generated_at).toLocaleTimeString('pt-BR')
    },
    activeSteps() {
      const byStep = this.d.active_by_step || {}
      return Object.keys(byStep).map(key => ({
        key,
        total: byStep[key],
        label: this.$te(`dashboard.steps.${key}`) ? this.$t(`dashboard.steps.${key}`) : key,
        color: STEP_COLORS[key] || '#b4bdc6',
      }))
    },
    avgTiles() {
      const { d } = this
      return [
        {
          key: 'solicita',
          step: 'solicita',
          icon: 'PhoneIncomingIcon',
          label: this.$t('dashboard.avg.solicita'),
          hint: this.$t('dashboard.avg.solicitaHint'),
          value: d.avg_solicita_seconds,
        },
        {
          key: 'attend',
          step: 'regulacao',
          icon: 'HeadphonesIcon',
          label: this.$t('dashboard.avg.attend'),
          hint: this.$t('dashboard.avg.attendHint'),
          value: d.avg_attend_to_request_seconds,
        },
        {
          key: 'dispatch',
          step: 'despacha',
          icon: 'SendIcon',
          label: this.$t('dashboard.avg.dispatch'),
          hint: this.$t('dashboard.avg.dispatchHint'),
          value: d.avg_dispatch_seconds,
        },
        {
          key: 'response',
          step: null,
          icon: 'MapPinIcon',
          label: this.$t('dashboard.avg.response'),
          hint: this.$t('dashboard.avg.responseHint'),
          value: d.avg_response_seconds,
        },
      ].map(t => ({ ...t, variant: this.avgVariant(t.value, t.step) }))
    },
  },
  watch: {
    // Guarda: a troca de nó hoje remonta a tela, mas não dependemos disso
    nodeId(id, old) {
      if (id === old) return
      this.unsubscribe()
      this.subscribe()
      this.d = {}
      this.loading = true
      this.loadOverview()
    },
  },
  mounted() {
    this.loadOverview()
    this.subscribe()
    this.watchRealtimeResync(() => this.loadOverview())
    this.startFallback()

    document.addEventListener('visibilitychange', this.onVisibilityChange)
    document.addEventListener('fullscreenchange', this.onFullscreenChange)
  },
  beforeDestroy() {
    this.stopFallback()
    this.unsubscribe()
    if (this.staleRetryTimer) clearTimeout(this.staleRetryTimer)
    document.removeEventListener('visibilitychange', this.onVisibilityChange)
    document.removeEventListener('fullscreenchange', this.onFullscreenChange)
  },
  methods: {
    formatSeconds,

    fmt(v) {
      return v == null ? '—' : v
    },

    async loadOverview() {
      // Com a aba oculta não buscamos nada; ao voltar, recarrega
      if (document.hidden) return
      if (this.requesting) {
        this.reloadQueued = true
        return
      }
      this.requesting = true
      try {
        const res = await this.$http.get(`${this.$store.state.api}dashboard/overview`)
        if (res.data && res.data.generated_at !== this.d.generated_at) this.d = res.data
        this.stale = false
        this.loadError = null
        this.retryIfSnapshotPredatesEvent()
      } catch (e) {
        // Painel informativo: mantém o último estado e sinaliza desatualização
        this.stale = true
        this.loadError = this.apiErrorText(e)
      } finally {
        this.requesting = false
        this.loading = false
        if (this.reloadQueued) {
          this.reloadQueued = false
          this.loadOverview()
        }
      }
    },

    // O backend cacheia por alguns segundos: se o snapshot recebido é anterior
    // ao último evento, busca de novo quando o cache expirar
    retryIfSnapshotPredatesEvent() {
      const generated = Date.parse(this.d.generated_at)
      if (!this.lastEventAt || Number.isNaN(generated) || generated >= this.lastEventAt) return
      if (this.staleRetryTimer) return
      this.staleRetryTimer = setTimeout(() => {
        this.staleRetryTimer = null
        this.loadOverview()
      }, STALE_RETRY_MS)
    },

    reloadSoon() {
      this.lastEventAt = serverNow()
      this.scheduleRealtimeReload(() => this.loadOverview(), EVENT_DEBOUNCE_MS)
    },

    // Hooks dos mixins de tempo real
    applyOccurrenceFeed() {
      this.clearOccurrenceFeed()
      this.reloadSoon()
    },
    onDispatchEvent() {
      this.reloadSoon()
    },

    subscribe() {
      this.watchOccurrenceFeed()
      this.watchDispatchEvents()
      this.watchTeamRequested()
    },

    unsubscribe() {
      this.unwatchOccurrenceFeed()
      this.unwatchDispatchEvents()
      this.unwatchTeamRequested()
    },

    // TeamRequestsMixin toca som e exibe toast; aqui só precisamos do sinal
    watchTeamRequested() {
      if (typeof window.Echo === 'undefined' || this.teamRequestedListener || !this.nodeId) return
      this.teamRequestedChannel = `node.${this.nodeId}`
      this.teamRequestedListener = () => this.reloadSoon()
      window.Echo.private(this.teamRequestedChannel).listen('.TeamRequested', this.teamRequestedListener)
    },

    unwatchTeamRequested() {
      if (typeof window.Echo === 'undefined' || !this.teamRequestedChannel) return
      // stopListening e nao leave(): o canal do no e compartilhado
      window.Echo.private(this.teamRequestedChannel).stopListening('.TeamRequested', this.teamRequestedListener)
      this.teamRequestedChannel = null
      this.teamRequestedListener = null
    },

    startFallback() {
      this.stopFallback()
      this.fallbackTimer = setInterval(() => this.loadOverview(), FALLBACK_MS)
    },

    stopFallback() {
      if (this.fallbackTimer) clearInterval(this.fallbackTimer)
      this.fallbackTimer = null
    },

    onVisibilityChange() {
      if (document.hidden) {
        this.stopFallback()
        return
      }
      this.loadOverview()
      this.startFallback()
    },

    avgVariant(seconds, step) {
      if (seconds == null || !step) return 'secondary'
      const { warning, danger } = resolveStepThresholds(this.$store.state.step_alerts || {}, step)
      if (seconds < warning) return 'success'
      if (seconds < danger) return 'warning'
      return 'danger'
    },

    toggleFullscreen() {
      if (document.fullscreenElement) {
        document.exitFullscreen()
        return
      }
      const el = this.$refs.board
      if (el && el.requestFullscreen) el.requestFullscreen()
    },

    onFullscreenChange() {
      this.isFullscreen = document.fullscreenElement === this.$refs.board
    },
  },
}
</script>

<style scoped>
.dashboard__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 1rem;
}

.dashboard__updated {
  font-size: 12px;
  color: #82868b;
  font-variant-numeric: tabular-nums;
}

.dashboard__section {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6e6b7b;
  margin: 0.5rem 0 0.75rem;
}

.dashboard__steps {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 10px;
  margin-top: 4px;
  font-size: 12px;
}

.dashboard__step {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
}

.dashboard__step-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

/* Modo TV: o container em tela cheia precisa do próprio fundo */
.dashboard--tv {
  background: #f8f8f8;
  padding: 24px 32px;
  overflow-y: auto;
}
.dark-layout .dashboard--tv {
  background: #161d31;
}
.dashboard--tv >>> .kpi-tile__value {
  font-size: 2.6rem;
}
.dashboard--tv >>> .kpi-tile__label {
  font-size: 13px;
}
.dashboard--tv .dashboard__title {
  font-size: 1.8rem;
}

.dark-layout .dashboard__section {
  color: #b4b7bd;
}
</style>
